import React from 'react';
import { mutate } from 'swr';

import useCurrentUserAndSettings from 'hooks/useCurrentUserAndSettings';
import useHandleError from 'hooks/useHandleError';
import { checkStatus, get, post, put } from 'libs/apiClient';
import { HttpStatusCode } from 'model/types';
import type {
  TableColumnSettings,
  UserSettingsViewModel,
} from 'model/UserSettings/types';
import { TABLE_COLUMN_VISIBILITY_SETTING_TYPE } from 'model/UserSettings/types';
import { mergeTableColumnSettings } from 'model/UserSettings/utils';

import { useApi } from './base-api';

type TableColumnSettingsRecord =
  UserSettingsViewModel<TableColumnSettings> | null;

export const tableColumnSettingsKey = `api/user-settings/current/${TABLE_COLUMN_VISIBILITY_SETTING_TYPE}`;

const getSettingsConfig = { shouldRetryOnError: false };

/**
 * Returns `undefined` until the user has saved something: with no record the
 * endpoint answers with an empty body, which the global fetcher's blind
 * `response.json()` turns into an error. `useHandleError` ignores a 204, and
 * `silentError` covers the rest — column visibility is cosmetic, so a read
 * failure must not toast on every page load. Note the flip side: it also hides
 * genuine failures of this key, so debug the write path from the network tab,
 * not from the absence of a toast.
 */
export const useGetTableColumnSettings = (skip?: boolean) =>
  useApi<TableColumnSettingsRecord>(
    skip ? null : tableColumnSettingsKey,
    getSettingsConfig,
    true
  );

// The endpoint has no upsert: POST always inserts, PUT 404s without a record,
// so the write path has to remember whether one exists. Module scope because
// the cache key is a singleton too — every table writes the same document.
let recordId: string | null = null;
let latestSettings: TableColumnSettings = {};
let persistTimeout: ReturnType<typeof setTimeout> | null = null;
let persistQueue: Promise<void> = Promise.resolve();
let persistOwnerId: string | null = null;
let persistGeneration = 0;

const persistDelay = 400;

/**
 * Switching company profile never reloads the document, and the app-wide guard
 * for it (`mutate(() => true, undefined)` on `company-changed-user-event`)
 * clears the SWR cache only — it cannot reach module scope. Without this, the
 * next toggle would carry the previous profile's `recordId` and settings into a
 * write made under the new profile.
 *
 * `persistQueue` is deliberately left alone: re-seeding it would let a write
 * that is already awaiting a response run alongside the next one, and two
 * concurrent writes against an endpoint with no upsert insert two documents.
 * Bumping the generation is what stops the in-flight one instead.
 */
const resetPersistState = () => {
  if (persistTimeout) {
    clearTimeout(persistTimeout);
    persistTimeout = null;
  }

  persistGeneration += 1;
  recordId = null;
  latestSettings = {};
};

/**
 * Returns a setter that merges into the stored blob and persists it. Writes are
 * debounced (toggling several columns is one request) and queued, so the
 * initial POST always resolves — and sets `recordId` — before a later write can
 * decide between POST and PUT.
 */
export const useSaveTableColumnSettings = () => {
  const { currentUser } = useCurrentUserAndSettings();
  const handleError = useHandleError();
  // Both halves: `userCompanyProfileId` is the per-company identity the switch
  // in `Layout` moves between, while `id` covers signing in as someone else.
  // Whichever of the two the backend rotates on a given switch, the pair
  // changes — settings must never carry over either way.
  const ownerId = currentUser
    ? `${currentUser.id}:${currentUser.userCompanyProfileId}`
    : null;

  React.useEffect(() => {
    // A null owner is "not known yet", not "somebody else": a silent-renew
    // flicker nulls the OIDC user for a moment, and resetting on that would
    // throw away a pending write for no reason. The real switch resolves to the
    // next profile, which is caught here.
    if (!ownerId) {
      return;
    }

    if (persistOwnerId !== null && persistOwnerId !== ownerId) {
      resetPersistState();
    }

    persistOwnerId = ownerId;
  }, [ownerId]);

  return React.useCallback(
    (update: (current: TableColumnSettings) => TableColumnSettings) => {
      mutate<TableColumnSettingsRecord>(
        tableColumnSettingsKey,
        (current) => {
          // Resolved against the cache rather than a rendered value, so a
          // second toggle within the same debounce window builds on the first.
          latestSettings = update(current?.settings ?? {});
          // `||`: an optimistic entry carries an empty id until the POST answers.
          recordId = current?.id || recordId;

          return {
            companyId: currentUser?.companyId ?? '',
            id: recordId ?? '',
            type: TABLE_COLUMN_VISIBILITY_SETTING_TYPE,
            userId: currentUser?.id ?? '',
            ...current,
            settings: latestSettings,
          };
        },
        { revalidate: false }
      );

      if (persistTimeout) {
        clearTimeout(persistTimeout);
      }

      persistTimeout = setTimeout(() => {
        persistQueue = persistQueue.then(async () => {
          // Re-checked after every await, not just on entry: the profile can
          // change while a request is in flight, and a continuation that resumed
          // afterwards would re-arm `recordId` and `latestSettings` with the
          // previous profile's values — undoing the reset that cancelled it.
          const generation = persistGeneration;
          const isStale = () => generation !== persistGeneration;

          if (isStale()) {
            return;
          }

          try {
            if (!recordId) {
              // The first toggle can land before the initial read resolves, and
              // POSTing then would insert a second document for the same user.
              const existing = await get(tableColumnSettingsKey);
              // Read as text, not `.json()`: "no record" comes back as an empty
              // body, and parsing that throws a bare SyntaxError that aborts the
              // write before it is ever attempted. `checkStatus` inside `get`
              // has already rejected anything that is not 2xx.
              const existingBody = await existing?.text();

              if (isStale()) {
                return;
              }

              const existingSettings = existingBody
                ? (JSON.parse(
                    existingBody
                  ) as UserSettingsViewModel<TableColumnSettings> | null)
                : null;

              recordId = existingSettings?.id ?? null;

              // The write replaces the whole document, and `latestSettings` is
              // resolved against the cache — which is equally empty when the
              // user has saved nothing and when the read failed (with
              // `shouldRetryOnError: false` that failure lasts the session).
              // Fold in what the server still holds, or the first toggle after
              // a failed read drops every other table's columns — and, since a
              // table's entry now holds visibility and order side by side, the
              // half this write isn't touching. `mergeTableColumnSettings`
              // merges per half, so a reorder can't discard hidden columns.
              if (existingSettings?.settings) {
                latestSettings = mergeTableColumnSettings(
                  existingSettings.settings,
                  latestSettings
                );

                mutate<TableColumnSettingsRecord>(
                  tableColumnSettingsKey,
                  (current) =>
                    current
                      ? { ...current, settings: latestSettings }
                      : current,
                  { revalidate: false }
                );
              }
            }

            const body = { settings: latestSettings };

            if (recordId) {
              const updated = await put(tableColumnSettingsKey, body);

              if (isStale()) {
                return;
              }

              if (updated?.status !== HttpStatusCode.NotFound) {
                checkStatus(updated);

                return;
              }

              // No upsert here, so a vanished record would otherwise leave the
              // id PUTting into nothing for the rest of the session. Drop it and
              // fall through to a fresh insert.
              recordId = null;
            }

            const inserted = await post(tableColumnSettingsKey, body);

            // Guard before `checkStatus`, as the PUT branch does: a stale 401
            // would otherwise call `signinRedirect()` and bounce a user who has
            // already re-authenticated as someone else.
            if (isStale()) {
              return;
            }

            const response = checkStatus(inserted);
            const created =
              (await response.json()) as UserSettingsViewModel<TableColumnSettings>;

            // Not redundant with the check above: reading the body is its own
            // await on a stream, so a profile switch can land inside it — and
            // what follows writes the new id to both `recordId` and the cache.
            if (isStale()) {
              return;
            }

            recordId = created.id;

            // Only the id — `settings` may already be behind another toggle.
            mutate<TableColumnSettingsRecord>(
              tableColumnSettingsKey,
              (current) => (current ? { ...current, id: created.id } : created),
              { revalidate: false }
            );
          } catch (error) {
            // Deliberately not rolling back: the optimistic state still works
            // for this session, and snapping the columns back seconds after the
            // click would be worse than losing the persistence.
            if (!isStale()) {
              handleError(error);
            }
          }
        });
      }, persistDelay);
    },
    [currentUser, handleError]
  );
};
